/**
 * Utility functions for handling time intervals in 5-minute increments
 */

/**
 * Convert decimal hours to hours and minutes
 * @param {number} decimalHours - Hours in decimal format (e.g., 1.5)
 * @returns {object} - Object with hours and minutes (e.g., { hours: 1, minutes: 30 })
 */
export const decimalToHoursMinutes = (decimalHours) => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return { hours, minutes };
};

/**
 * Convert hours and minutes to decimal hours
 * @param {number} hours - Whole hours
 * @param {number} minutes - Minutes (0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55)
 * @returns {number} - Decimal hours (e.g., 1.5)
 */
export const hoursMinutesToDecimal = (hours, minutes) => {
  return hours + (minutes / 60);
};

/**
 * Format decimal hours as "Xh Ym" string
 * @param {number} decimalHours - Hours in decimal format
 * @returns {string} - Formatted string (e.g., "1h 30m", "2h 0m", "0h 45m")
 */
export const formatHoursMinutes = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return '0h 0m';
  
  const { hours, minutes } = decimalToHoursMinutes(decimalHours);
  return `${hours}h ${minutes}m`;
};

/**
 * Generate time interval options in 5-minute increments
 * @param {number} maxHours - Maximum hours to generate options for (default: 200)
 * @returns {Array} - Array of objects with value (decimal) and label (formatted string)
 */
export const generateTimeIntervalOptions = (maxHours = 200) => {
  const options = [];

  // Start with 0
  options.push({ value: 0, label: '0h 0m' });

  // Generate intervals: 0.0833, 0.1667, 0.25, 0.3333, etc. (5-minute increments)
  for (let h = 0; h <= maxHours; h++) {
    for (let m = 5; m <= 55; m += 5) {
      const decimalValue = h + (m / 60);
      if (decimalValue <= maxHours) {
        options.push({
          value: decimalValue,
          label: `${h}h ${m}m`
        });
      }
    }

    // Add full hour (except 0 which we already added)
    if (h > 0 && h <= maxHours) {
      options.push({
        value: h,
        label: `${h}h 0m`
      });
    }
  }

  // Sort by value to ensure proper order
  options.sort((a, b) => a.value - b.value);

  return options;
};

/**
 * Round a decimal hour value to the nearest 5-minute interval
 * @param {number} decimalHours - Hours in decimal format
 * @returns {number} - Rounded decimal hours
 */
export const roundToNearestInterval = (decimalHours) => {
  // Round to nearest 0.0833... (5 minutes = 1/12 hour)
  return Math.round(decimalHours * 12) / 12;
};

